import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useAlert,
  useAuth,
  useSessionStorage,
} from '@spotify/confidence-core-react';

import {
  CreateAccountFormContext,
  type SignupScreen,
} from './createAccountFormContexts';
import { getSignupAttribution } from './hooks/attribution';
import { useAuthConnections } from './hooks/useAuthConnections';
import {
  AuthConnection,
  CreateAccountRequest,
  Region,
  useCreateAccount,
} from './hooks/useCreateAccount';
import { useEmailVerification } from './hooks/useEmailVerification';
import type { CreatedAccount } from './hooks/useInvite';
import { useSignupTracking } from './hooks/useSignupTracking';
import { useWorkspaceNameFields } from './hooks/useWorkspaceNameFields';
import {
  AccountDetailsState,
  DEFAULT_STATE,
  isAccountUnderReviewError,
  isEmailNotVerifiedError,
} from './signupUtils';

export type { SignupScreen } from './createAccountFormContexts';
export {
  useAccountDetailsContext,
  useAuthConnectionsContext,
  useCreateAccountFormContext,
  useWorkspaceNameFieldsContext,
} from './createAccountFormContexts';

type CreateAccountFormProviderProps = {
  inviteId?: string;
  token?: string;
  onCreate: (account?: CreatedAccount) => void;
  children: ReactNode;
};

export const CreateAccountFormProvider = ({
  inviteId,
  token,
  onCreate,
  children,
}: CreateAccountFormProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithPopup,
    getAccessTokenSilently,
    error: authError,
  } = useAuth();

  const [{ value: createdAccount, error }, createAccount] =
    useCreateAccount(inviteId);
  const [showOverlay, setShowOverlay] = useState(false);

  const {
    trackStep,
    trackInteraction,
    trackCompletion,
    setOrgContext,
    getPreviousStepId,
  } = useSignupTracking('slim_wizard', { enabled: isAuthenticated });

  useEffect(() => {
    if (authError && !isEmailNotVerifiedError(authError)) {
      alert.post({
        message: `Failed to log in: ${authError.message}`,
        severity: 'error',
      });
    }
  }, [authError, alert]);

  const signupAttribution = useMemo(
    () => getSignupAttribution(searchParams),
    [searchParams],
  );

  const [acceptMarketingEmails, setAcceptMarketingEmails] = useState(true);
  const [accountDetails, setAccountDetails] =
    useSessionStorage<AccountDetailsState>(
      `signup-slim-v1-${inviteId ?? 'regular'}`,
      DEFAULT_STATE,
    );

  useEffect(() => {
    if (createdAccount) {
      trackCompletion('success');
    }
  }, [createdAccount, trackCompletion]);

  const handleResultDismissed = useCallback(() => {
    setShowOverlay(false);
    if (createdAccount) {
      onCreate(createdAccount);
      setAccountDetails(DEFAULT_STATE);
    }
  }, [createdAccount, onCreate, setAccountDetails]);

  useEffect(() => {
    if (error) {
      trackCompletion('error', {
        error_message: error.message,
        final_step_id: 'workspace_setup',
      });
    }
  }, [error, trackCompletion]);

  const setField = useCallback(
    (newData: Partial<AccountDetailsState>) => {
      setAccountDetails(current => ({ ...current, ...newData }));
    },
    [setAccountDetails],
  );

  useEffect(() => {
    if (!user?.email) return;
    setField({ adminEmail: user.email });
  }, [user?.email, setField]);

  const {
    toggleAuthConnection: applyAuthConnectionToggle,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
  } = useAuthConnections({
    user,
    inviteId,
    authConnections: accountDetails.authConnections,
    setAccountDetails,
  });

  const {
    loginId,
    handleDisplayNameChange: applyDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    validLoginId,
    helperText,
    loginIdAvailable,
    loginIdError,
    loginIdConfirmedAvailable,
    isCheckingAvailability,
  } = useWorkspaceNameFields({
    displayName: accountDetails.displayName,
    setField,
    inviteId,
  });

  useEffect(() => {
    setOrgContext({
      display_name: accountDetails.displayName || undefined,
      workspace_name: loginId || undefined,
    });
  }, [accountDetails.displayName, loginId, setOrgContext]);

  const hasTrackedNameInputRef = useRef(false);
  const handleDisplayNameChange = useCallback(
    (value: string) => {
      if (!hasTrackedNameInputRef.current && value.trim() !== '') {
        hasTrackedNameInputRef.current = true;
        trackInteraction({
          control_id: 'workspace_name',
          control_type: 'text_input',
        });
      }
      applyDisplayNameChange(value);
    },
    [applyDisplayNameChange, trackInteraction],
  );

  const handleRegionChange = useCallback(
    (region: Region) => {
      trackInteraction({
        control_id: 'storage_region',
        control_type: 'radio',
        value: region,
      });
      setField({ region });
    },
    [setField, trackInteraction],
  );

  const toggleAuthConnection = useCallback(
    (connection: AuthConnection) => {
      const applyToggle = applyAuthConnectionToggle(connection);
      return (e: SyntheticEvent<EventTarget>, checked: boolean) => {
        trackInteraction({
          control_id: 'auth_connection',
          control_type: 'toggle_checkbox',
          value: connection,
          checked,
        });
        applyToggle(e, checked);
      };
    },
    [applyAuthConnectionToggle, trackInteraction],
  );

  const handleAcceptMarketingEmailsChange = useCallback(
    (checked: boolean) => {
      trackInteraction({
        control_id: 'marketing_emails',
        control_type: 'toggle_checkbox',
        checked,
      });
      setAcceptMarketingEmails(checked);
    },
    [trackInteraction],
  );

  const hasCleanedStepParam = useRef(false);
  useEffect(() => {
    if (hasCleanedStepParam.current || !searchParams.has('step')) return;
    hasCleanedStepParam.current = true;
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('step');
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams]);

  const isEmailVerified = user?.email_verified ?? false;

  const deriveScreen = (): SignupScreen => {
    if (isLoading) return 'loading';
    if (isAuthenticated && !isEmailVerified) return 'verify_email';
    if (isAuthenticated && isAccountUnderReviewError(error)) {
      return 'under_review';
    }
    if (isAuthenticated) return 'workspace_setup';
    if (isEmailNotVerifiedError(authError)) return 'login_verify_email';
    return 'welcome';
  };
  const screen = deriveScreen();

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (screen !== 'workspace_setup' || hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    trackStep('impression', {
      step_id: 'workspace_setup',
      step_name: 'Workspace Setup',
      step_index: 0,
      step_count: 1,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const handleLogin = useCallback(async () => {
    await loginWithPopup(
      { authorizationParams: { screen_hint: 'signup' } },
      { timeoutInSeconds: 600 },
    );
  }, [loginWithPopup]);

  const handleCreate = useCallback(() => {
    if (!user) return;
    trackInteraction({
      control_id: 'create_workspace',
      control_type: 'button',
      form_state: {
        storage_region: accountDetails.region,
        marketing_emails: acceptMarketingEmails,
        auth_connections: Object.entries(accountDetails.authConnections)
          .filter(([, enabled]) => enabled)
          .map(([connection]) => connection),
      },
    });
    setShowOverlay(true);

    createAccount(
      {
        displayName: accountDetails.displayName,
        loginId: loginId,
        region: accountDetails.region,
        authConnections: Object.entries(accountDetails.authConnections).flatMap(
          ([connection, enabled]) => (enabled ? [{ [connection]: {} }] : []),
        ) as CreateAccountRequest['authConnections'],
        adminEmail:
          accountDetails.adminEmail !== ''
            ? accountDetails.adminEmail
            : undefined,
        allowedLoginEmailDomains:
          inviteId && accountDetails.authConnections.googleAuthConnection
            ? [accountDetails.loginDomain].filter(
                (domain): domain is string => !!domain,
              )
            : undefined,
        marketingOptIn: acceptMarketingEmails,
        signupAttribution,
      },
      token,
    );
  }, [
    user,
    accountDetails,
    loginId,
    inviteId,
    acceptMarketingEmails,
    signupAttribution,
    token,
    createAccount,
    trackInteraction,
  ]);

  const hasTrackedVerifyEmailRef = useRef(false);
  useEffect(() => {
    if (screen !== 'verify_email' || hasTrackedVerifyEmailRef.current) return;
    hasTrackedVerifyEmailRef.current = true;
    trackStep('impression', {
      step_id: 'verify_email',
      step_name: 'Verify Email',
      step_index: -1,
      step_count: 2,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const { recheckEmailVerification } = useEmailVerification({
    getAccessTokenSilently,
    isAuthenticated,
    isEmailVerified,
  });

  const canSubmit =
    !!loginId &&
    loginIdAvailable &&
    validDisplayName &&
    validLoginId &&
    !!accountDetails.displayName &&
    (!showAuthConnections || hasSelectedAuthConnection) &&
    !showOverlay;

  const contextValue = useMemo(
    () => ({
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      loginId,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      validLoginId,
      helperText,
      loginIdAvailable,
      loginIdError,
      loginIdConfirmedAvailable,
      isCheckingAvailability,
      screen,
      error,
      isAccountCreated: !!createdAccount,
      showOverlay,
      canSubmit,
      acceptMarketingEmails,
      setAcceptMarketingEmails: handleAcceptMarketingEmailsChange,
      handleLogin,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    }),
    [
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      loginId,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      validLoginId,
      helperText,
      loginIdAvailable,
      loginIdError,
      loginIdConfirmedAvailable,
      isCheckingAvailability,
      screen,
      error,
      createdAccount,
      showOverlay,
      canSubmit,
      acceptMarketingEmails,
      handleAcceptMarketingEmailsChange,
      handleLogin,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    ],
  );

  return (
    <CreateAccountFormContext.Provider value={contextValue}>
      {children}
    </CreateAccountFormContext.Provider>
  );
};
